import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { paymentAPI, maintenanceAPI, leaseAPI, invoiceAPI, authAPI, autopayAPI, documentAPI, moveoutAPI } from '../services/api';import { DollarSign, Home, Wrench, FileText, CreditCard, Calendar, Loader2, Receipt, RefreshCw, CheckCircle, XCircle, Download, BookOpen, Pen, Camera, X, Image } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from '../hooks/use-toast';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardNumberElement, CardExpiryElement, CardCvcElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { Badge } from '../components/ui/badge';
import LeaseSignature from '../components/LeaseSignature';
import PasswordChangeModal from '../components/PasswordChangeModal';
import NotificationSettings from '../components/NotificationSettings';
import MoveOutChecklist from '../components/MoveOutChecklist';

// Initialize Stripe with publishable key - only if key exists
const stripeKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
const stripePromise = stripeKey ? loadStripe(stripeKey) : null;

// Card element styling — larger font + more padding for mobile touch targets
const cardElementOptions = {
  style: {
    base: {
      fontSize: '17px',
      fontFamily: 'system-ui, -apple-system, sans-serif',
      color: '#0f172a',
      iconColor: '#16a34a',
      '::placeholder': {
        color: '#94a3b8',
      },
      lineHeight: '28px',
    },
    invalid: {
      color: '#dc2626',
      iconColor: '#dc2626',
    },
  },
};

// Split card fields — card number on its own row, expiry + CVC on a
// second row. Prevents the mobile-overlap issue Gregory hit with the
// single-line CardElement (card number, expiry, CVC and ZIP all cramming
// into one row on iPhone).
const SplitCardFields = ({ disabled = false }) => (
  <div className="space-y-2" data-testid="split-card-fields">
    <div>
      <label className="block text-xs font-medium text-gray-600 mb-1">Card Number</label>
      <div
        className={`p-3 border rounded-md bg-white ${disabled ? 'opacity-60' : ''}`}
        data-testid="card-number-wrapper"
      >
        <CardNumberElement options={{ ...cardElementOptions, showIcon: true }} />
      </div>
    </div>
    <div className="grid grid-cols-2 gap-3">
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">Expiration (MM / YY)</label>
        <div
          className={`p-3 border rounded-md bg-white ${disabled ? 'opacity-60' : ''}`}
          data-testid="card-expiry-wrapper"
        >
          <CardExpiryElement options={cardElementOptions} />
        </div>
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">CVC</label>
        <div
          className={`p-3 border rounded-md bg-white ${disabled ? 'opacity-60' : ''}`}
          data-testid="card-cvc-wrapper"
        >
          <CardCvcElement options={cardElementOptions} />
        </div>
      </div>
    </div>
  </div>
);

// Stripe Payment Form Component
const StripePaymentForm = ({ leaseId, invoice, autopayEnabledHint = false, onSuccess, onCancel }) => {
  const stripe = useStripe();
  const elements = useElements();

  // Derive a safe, always-non-empty description. Older deposit invoices in
  // the DB have description=null which used to cause the backend Pydantic
  // model to reject the request (422), so Stripe charged the card while our
  // app never recorded the payment.
  const defaultDescription = (() => {
    if (invoice?.description && String(invoice.description).trim()) {
      return invoice.description;
    }
    const t = invoice?.invoice_type;
    if (t === 'deposit') return 'Security Deposit Payment';
    if (t === 'rent') return 'Rent Payment';
    if (t) return `${String(t).charAt(0).toUpperCase()}${String(t).slice(1)} Payment`;
    return 'Rent Payment';
  })();

  const [amount, setAmount] = useState(
    invoice && (invoice.amount_due != null)
      ? String(invoice.amount_due)
      : ''
  );
  const [description, setDescription] = useState(defaultDescription);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  // Save-card-for-autopay opt-in — after successful payment, offer to
  // save the used card so future rent is auto-charged.
  const [saveCard, setSaveCard] = useState(false);
  // Success confirmation state (populated after a successful charge)
  const [successState, setSuccessState] = useState(null);
  // Whether the save-for-autopay call succeeded/failed independent of
  // the underlying payment.
  const [autopayResult, setAutopayResult] = useState(null);

  // Calculate convenience fee (3%)
  const convenienceFeeRate = 0.03;
  const baseAmount = parseFloat(amount) || 0;
  const convenienceFee = Math.round(baseAmount * convenienceFeeRate * 100) / 100;
  const totalAmount = Math.round((baseAmount + convenienceFee) * 100) / 100;

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    if (!amount || parseFloat(amount) <= 0) {
      setError('Please enter a valid amount');
      return;
    }

    setProcessing(true);
    setError(null);

    try {
      // 1. Create payment intent on the backend
      const intentResponse = await paymentAPI.createPaymentIntent(parseFloat(amount), leaseId);
      const { client_secret, payment_intent_id } = intentResponse.data;

      // 2. Confirm payment with Stripe
      const { error: stripeError, paymentIntent } = await stripe.confirmCardPayment(client_secret, {
        payment_method: {
          card: elements.getElement(CardNumberElement),
        },
      });

      if (stripeError) {
        setError(stripeError.message);
        setProcessing(false);
        return;
      }

      // 3. If payment succeeded, record it in our backend
      if (paymentIntent.status === 'succeeded') {
        // Process payment - backend will update the invoice if invoice_id is provided
        await paymentAPI.processPayment({
          payment_intent_id: payment_intent_id,
          lease_id: leaseId,
          amount: parseFloat(amount),
          description: (description && String(description).trim()) || 'Payment',
          invoice_id: invoice ? (invoice.id || invoice._id) : null
        });

        // Fetch card brand + last-4 for the confirmation screen (safe read
        // from Stripe via our backend, no card data touches our DB).
        let cardBrand = null;
        let cardLast4 = null;
        try {
          const cardRes = await paymentAPI.getIntentCard(payment_intent_id);
          cardBrand = cardRes.data?.brand || null;
          cardLast4 = cardRes.data?.last4 || null;
        } catch (_e) { /* non-blocking */ }

        // If the tenant opted-in to save the card for autopay, run the
        // SetupIntent flow with the SAME elements before we clear them.
        let apResult = null;
        if (saveCard) {
          try {
            const setupRes = await autopayAPI.setup();
            const { client_secret: apSecret, setup_intent_id } = setupRes.data;
            const { error: apError } = await stripe.confirmCardSetup(apSecret, {
              payment_method: {
                card: elements.getElement(CardNumberElement),
              },
            });
            if (apError) {
              apResult = { ok: false, message: apError.message };
            } else {
              const confirmRes = await autopayAPI.confirm(setup_intent_id);
              apResult = {
                ok: true,
                last_four: confirmRes.data?.last_four || cardLast4,
                brand: confirmRes.data?.card_brand || cardBrand,
              };
            }
          } catch (apErr) {
            apResult = { ok: false, message: apErr.response?.data?.detail || 'Could not save card for autopay' };
          }
        }
        setAutopayResult(apResult);

        // Show the confirmation screen — the parent onSuccess() is called
        // when the user taps "Done" on the confirmation screen.
        setSuccessState({
          amount: baseAmount,
          totalCharged: totalAmount,
          convenienceFee,
          description: (description && String(description).trim()) || 'Payment',
          invoice_id: invoice ? (invoice.id || invoice._id) : null,
          invoice_description: invoice?.description,
          card_brand: cardBrand,
          card_last4: cardLast4,
          autopay_saved: !!(apResult && apResult.ok),
        });

        toast({
          title: "Payment Successful! ✅",
          description: `$${totalAmount.toFixed(2)} charged${cardLast4 ? ` to ${cardBrand || 'card'} •••• ${cardLast4}` : ''}.`,
          variant: "success"
        });
      }
    } catch (err) {
      console.error('Payment error:', err);
      setError(err.response?.data?.detail || 'Payment failed. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <>
      {successState && (
        <div className="space-y-4" data-testid="payment-success-screen">
          <div className="flex flex-col items-center text-center py-4">
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mb-3">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900">Payment Successful</h3>
            <p className="text-sm text-gray-500 mt-1">
              Charged{successState.card_last4 ? (
                <> to <b>{successState.card_brand ? successState.card_brand.charAt(0).toUpperCase() + successState.card_brand.slice(1) : 'Card'} •••• {successState.card_last4}</b></>
              ) : ' successfully'}
            </p>
          </div>

          <div className="p-4 bg-gray-50 border rounded-lg space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Payment</span>
              <span className="font-medium">${successState.amount.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-orange-600">
              <span>Convenience fee (3%)</span>
              <span>+${successState.convenienceFee.toFixed(2)}</span>
            </div>
            <div className="flex justify-between font-semibold border-t pt-2">
              <span>Total charged</span>
              <span className="text-green-700">${successState.totalCharged.toFixed(2)}</span>
            </div>
            <div className="flex justify-between pt-2 border-t">
              <span className="text-gray-600">For</span>
              <span className="text-right ml-4 truncate max-w-[70%]">
                {successState.invoice_description || successState.description}
              </span>
            </div>
          </div>

          {successState.autopay_saved && (
            <div className="p-3 bg-green-50 border border-green-200 rounded-md text-sm text-green-800 flex items-start gap-2" data-testid="autopay-saved-banner">
              <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <div>
                <div className="font-medium">Autopay is on</div>
                <div className="text-xs text-green-700">
                  Next rent will auto-charge to {successState.card_brand ? successState.card_brand.charAt(0).toUpperCase() + successState.card_brand.slice(1) : 'card'} •••• {successState.card_last4 || '••••'}. Turn off anytime in Settings.
                </div>
              </div>
            </div>
          )}
          {saveCard && autopayResult && !autopayResult.ok && (
            <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-md text-sm text-yellow-900" data-testid="autopay-save-failed-banner">
              Your payment went through, but we couldn&apos;t save the card for autopay: {autopayResult.message || 'unknown error'}. You can set up autopay from Settings later.
            </div>
          )}

          <Button
            type="button"
            className="w-full bg-green-600 hover:bg-green-700"
            onClick={() => onSuccess()}
            data-testid="payment-done-btn"
          >
            Done
          </Button>
        </div>
      )}

      {!successState && (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <Label>Payment Amount ($)</Label>
        <Input
          type="number"
          step="0.01"
          min="0.01"
          max={invoice ? invoice.amount_due : undefined}
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          placeholder="350.00"
          required
          disabled={processing}
          data-testid="payment-amount-input"
        />
        {invoice && invoice.amount_due > 0 && (
          <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
            <span className="text-gray-600">
              💡 You can pay any amount up to <b>${Number(invoice.amount_due).toFixed(2)}</b>. Partial payments are welcome — the rest stays as a balance.
            </span>
            <div className="flex items-center gap-1 mt-1 sm:mt-0">
              {[0.25, 0.5, 0.75, 1].map(pct => {
                const val = (Number(invoice.amount_due) * pct).toFixed(2);
                const label = pct === 1 ? 'Full' : `${pct * 100}%`;
                return (
                  <button
                    key={pct}
                    type="button"
                    onClick={() => setAmount(val)}
                    disabled={processing}
                    className="px-2 py-0.5 text-xs rounded border border-green-300 text-green-700 hover:bg-green-50"
                    data-testid={`payment-preset-${label.toLowerCase()}`}
                  >
                    {label} · ${val}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
      
      {/* Fee Breakdown */}
      {baseAmount > 0 && (
        <div className="p-3 bg-gray-50 border rounded-md space-y-1 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-600">Payment Amount:</span>
            <span>${baseAmount.toFixed(2)}</span>
          </div>
          <div className="flex justify-between text-orange-600">
            <span>Convenience Fee (3%):</span>
            <span>+${convenienceFee.toFixed(2)}</span>
          </div>
          <div className="flex justify-between font-semibold border-t pt-1 mt-1">
            <span>Total to be charged:</span>
            <span className="text-green-600">${totalAmount.toFixed(2)}</span>
          </div>
        </div>
      )}
      
      <div>
        <Label>Description</Label>
        <Input
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Rent Payment"
          disabled={processing}
        />
      </div>
      <div>
        <Label>Card Details</Label>
        <SplitCardFields disabled={processing} />
      </div>

      {/* Save card for autopay — appears only if the tenant does not
          already have autopay enabled (autopayEnabledHint prop). */}
      {!autopayEnabledHint && (
        <label className="flex items-start gap-2 p-3 bg-emerald-50 border border-emerald-200 rounded-md cursor-pointer" data-testid="save-card-for-autopay-wrapper">
          <input
            type="checkbox"
            checked={saveCard}
            onChange={(e) => setSaveCard(e.target.checked)}
            disabled={processing}
            className="mt-0.5 h-4 w-4 accent-emerald-600"
            data-testid="save-card-for-autopay-checkbox"
          />
          <span className="text-sm text-emerald-900">
            <b>Save this card for autopay</b>
            <span className="block text-xs text-emerald-700 mt-0.5">
              Rent auto-charges each month — you can turn it off any time in Settings.
            </span>
          </span>
        </label>
      )}

      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-md text-red-600 text-sm">
          {error}
        </div>
      )}
      <div className="flex gap-3">
        <Button
          type="submit"
          disabled={!stripe || processing}
          className="flex-1 bg-green-600 hover:bg-green-700"
        >
          {processing ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Processing...
            </>
          ) : (
            <>
              <CreditCard className="mr-2 h-4 w-4" />
              Pay ${totalAmount > 0 ? totalAmount.toFixed(2) : '0.00'}
            </>
          )}
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={processing}
        >
          Cancel
        </Button>
      </div>
      <p className="text-xs text-gray-500 text-center">
        Secure payment powered by Stripe
      </p>
    </form>
      )}
    </>
  );
};

// Autopay Setup Form Component
const AutopaySetupForm = ({ onSuccess, onCancel, submitLabel = 'Enable Autopay' }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!stripe || !elements) {
      return;
    }

    setProcessing(true);
    setError(null);

    try {
      // 1. Create SetupIntent on backend
      const setupResponse = await autopayAPI.setup();
      const { client_secret, setup_intent_id } = setupResponse.data;

      // 2. Confirm SetupIntent with Stripe
      const { error: stripeError, setupIntent } = await stripe.confirmCardSetup(client_secret, {
        payment_method: {
          card: elements.getElement(CardNumberElement),
        },
      });

      if (stripeError) {
        setError(stripeError.message);
        setProcessing(false);
        return;
      }

      // 3. Confirm with backend
      const confirmResponse = await autopayAPI.confirm(setup_intent_id);
      
      toast({
        title: "Autopay Enabled!",
        description: `Your card ending in ${confirmResponse.data.last_four} is now set up for automatic payments.`
      });
      
      onSuccess(confirmResponse.data);
    } catch (err) {
      console.error('Autopay setup error:', err);
      setError(err.response?.data?.detail || 'Failed to set up autopay');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="p-4 bg-blue-50 rounded-lg mb-4">
        <h4 className="font-medium text-blue-800 mb-2">How Autopay Works</h4>
        <ul className="text-sm text-blue-700 space-y-1">
          <li>• Your card will be securely saved for future payments</li>
          <li>• Monthly rent will be automatically charged on the due date</li>
          <li>• You'll receive email confirmations for each payment</li>
          <li>• You can disable autopay at any time</li>
        </ul>
      </div>
      
      <div>
        <Label>Card Details</Label>
        <div className="mt-2">
          <SplitCardFields disabled={processing} />
        </div>
      </div>
      
      {error && (
        <div className="p-3 bg-red-50 text-red-600 rounded-md text-sm">
          {error}
        </div>
      )}
      
      <div className="flex gap-3">
        <Button
          type="submit"
          className="flex-1 bg-green-600 hover:bg-green-700"
          disabled={!stripe || processing}
        >
          {processing ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Setting up...
            </>
          ) : (
            <>
              <RefreshCw className="mr-2 h-4 w-4" />
              {submitLabel}
            </>
          )}
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={processing}
        >
          Cancel
        </Button>
      </div>
      <p className="text-xs text-gray-500 text-center">
        Your card is securely stored by Stripe
      </p>
    </form>
  );
};

const TenantDashboard = () => {
  const { user, setUser } = useAuth();
  const [payments, setPayments] = useState([]);
  const [maintenanceRequests, setMaintenanceRequests] = useState([]);
  const [leases, setLeases] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showStripePayment, setShowStripePayment] = useState(false);
  const [stripeInvoice, setStripeInvoice] = useState(null); // Invoice being paid via Stripe
  const [maintenanceForm, setMaintenanceForm] = useState({
    title: '',
    description: '',
    priority: 'Medium'
  });
  const [maintenancePhotos, setMaintenancePhotos] = useState([]);
  const [uploadingMaintenance, setUploadingMaintenance] = useState(false);
  
  // Profile editing state
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [profileForm, setProfileForm] = useState({
    email: '',
    phone: ''
  });
  const [updatingProfile, setUpdatingProfile] = useState(false);
  const [showAllPayments, setShowAllPayments] = useState(false);
  
  // Password change state (for temporary passwords)
  const [mustChangePassword, setMustChangePassword] = useState(false);
  
  // Autopay state
  const [autopayStatus, setAutopayStatus] = useState({
    enabled: false,
    has_payment_method: false,
    last_four: null,
    card_brand: null
  });
  const [showAutopaySetup, setShowAutopaySetup] = useState(false);
  const [showChangeCard, setShowChangeCard] = useState(false);
  const [loadingAutopay, setLoadingAutopay] = useState(false);

  useEffect(() => {
    fetchData();
    fetchAutopayStatus();
    checkPasswordChangeRequired();
  }, []);

  // Default the "Select invoice to pay" dropdown to the tenant's oldest
  // unpaid invoice — otherwise a partial online payment can land as a
  // "General Payment" (invoice_id=null) and the invoice balance never
  // decreases. Landlord reported this happening for Antonio's $100.
  useEffect(() => {
    if (stripeInvoice) return;
    const unpaid = (invoices || [])
      .filter(inv => inv.status !== 'Paid' && (inv.amount_due || 0) > 0)
      .sort((a, b) => new Date(a.due_date || 0) - new Date(b.due_date || 0));
    if (unpaid.length > 0) setStripeInvoice(unpaid[0]);
  }, [invoices, stripeInvoice]);
  
  useEffect(() => {
    if (user) {
      setProfileForm({
        email: user.email || '',
        phone: user.phone || ''
      });
      // Also check if user needs to change password
      if (user.must_change_password) {
        setMustChangePassword(true);
      }
    }
  }, [user]);
  
  // Check if password change is required
  const checkPasswordChangeRequired = async () => {
    try {
      const response = await authAPI.getMe();
      if (response.data.must_change_password) {
        setMustChangePassword(true);
      }
    } catch (error) {
      console.error('Error checking password status:', error);
    }
  };
  
  // Handle password change completion
  const handlePasswordChanged = () => {
    setMustChangePassword(false);
    // Update user state if setUser is available
    if (setUser && user) {
      setUser({ ...user, must_change_password: false });
    }
  };
  
  const fetchAutopayStatus = async () => {
    try {
      const response = await autopayAPI.getStatus();
      setAutopayStatus(response.data);
    } catch (error) {
      console.error('Error fetching autopay status:', error);
    }
  };
  
  const handleDisableAutopay = async () => {
    if (!window.confirm('Are you sure you want to disable automatic payments?')) {
      return;
    }
    
    setLoadingAutopay(true);
    try {
      await autopayAPI.disable();
      setAutopayStatus(prev => ({ ...prev, enabled: false }));
      toast({
        title: "Autopay Disabled",
        description: "Automatic payments have been turned off."
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to disable autopay",
        variant: "destructive"
      });
    } finally {
      setLoadingAutopay(false);
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const [paymentsRes, maintenanceRes, leasesRes, invoicesRes] = await Promise.all([
        paymentAPI.getPayments(),
        maintenanceAPI.getRequests(),
        leaseAPI.getLeases(),
        invoiceAPI.getInvoices()
      ]);
      
      setPayments(paymentsRes.data);
      setMaintenanceRequests(maintenanceRes.data);
      setLeases(leasesRes.data);
      setInvoices(invoicesRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast({
        title: "Error",
        description: "Failed to load dashboard data",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleMaintenanceSubmit = async (e) => {
    e.preventDefault();
    
    // Get the first lease to get room_id
    if (!leases || leases.length === 0) {
      toast({
        title: "Error",
        description: "No active lease found. Cannot submit maintenance request.",
        variant: "destructive"
      });
      return;
    }
    
    const activeLease = leases.find(l => l.status === 'Active') || leases[0];
    const roomId = activeLease?.room_id;
    
    if (!roomId) {
      toast({
        title: "Error",
        description: "No room associated with your lease. Please contact support.",
        variant: "destructive"
      });
      return;
    }
    
    setUploadingMaintenance(true);
    
    try {
      // First submit the maintenance request
      const response = await maintenanceAPI.submit({
        room_id: roomId,
        title: maintenanceForm.title,
        issue_type: maintenanceForm.title,
        description: maintenanceForm.description,
        priority: maintenanceForm.priority
      });
      
      const requestId = response.data.id || response.data._id;
      
      // If there are photos, upload them
      if (maintenancePhotos.length > 0 && requestId) {
        try {
          await maintenanceAPI.uploadPhotos(requestId, maintenancePhotos);
          toast({
            title: "Maintenance Request Submitted!",
            description: `Your request with ${maintenancePhotos.length} photo(s) has been submitted.`,
          });
        } catch (photoError) {
          console.error('Error uploading photos:', photoError);
          toast({
            title: "Request Submitted",
            description: "Your maintenance request was submitted, but photos failed to upload. Please contact support.",
            variant: "warning"
          });
        }
      } else {
        toast({
          title: "Maintenance Request Submitted!",
          description: "Our team will review your request shortly.",
        });
      }
      
      setMaintenanceForm({ title: '', description: '', priority: 'Medium' });
      setMaintenancePhotos([]);
      
      // Refresh data to show the new request
      fetchData();
    } catch (error) {
      console.error('Error submitting maintenance request:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to submit maintenance request",
        variant: "destructive"
      });
    } finally {
      setUploadingMaintenance(false);
    }
  };
  
  const handleMaintenancePhotoChange = (e) => {
    const files = Array.from(e.target.files);
    // Limit to 5 photos max
    if (files.length + maintenancePhotos.length > 5) {
      toast({
        title: "Too many photos",
        description: "You can upload a maximum of 5 photos per request.",
        variant: "destructive"
      });
      return;
    }
    setMaintenancePhotos([...maintenancePhotos, ...files]);
  };
  
  const removeMaintenancePhoto = (index) => {
    setMaintenancePhotos(maintenancePhotos.filter((_, i) => i !== index));
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setUpdatingProfile(true);
    try {
      const response = await authAPI.updateProfile({
        email: profileForm.email,
        phone: profileForm.phone
      });
      
      toast({
        title: "Profile Updated",
        description: "Your profile has been updated successfully."
      });
      
      setIsEditingProfile(false);
      // Refresh user data
      window.location.reload();
    } catch (error) {
      console.error('Error updating profile:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to update profile",
        variant: "destructive"
      });
    } finally {
      setUpdatingProfile(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 overflow-x-hidden">
      {/* Password Change Modal - Shows when user has temporary password */}
      <PasswordChangeModal 
        isOpen={mustChangePassword} 
        onPasswordChanged={handlePasswordChanged} 
      />
      
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-8">
        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Welcome back, {user?.name}!</h1>
          <p className="text-sm sm:text-base text-gray-600 mt-1 sm:mt-2">Manage your rental account and requests</p>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6 mb-6 sm:mb-8">
          <Card>
            <CardContent className="p-3 sm:pt-6 sm:p-6">
              <div className="flex items-center justify-between">
                <div className="min-w-0">
                  <p className="text-xs sm:text-sm text-gray-600">Current Room</p>
                  <p className="text-lg sm:text-2xl font-bold truncate">
                    {leases.length > 0 ? (leases[0]?.room_title || leases[0]?.room_number || 'Room') : 'No Lease'}
                  </p>
                </div>
                <Home className="h-6 w-6 sm:h-8 sm:w-8 text-blue-600 shrink-0" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-3 sm:pt-6 sm:p-6">
              <div className="flex items-center justify-between">
                <div className="min-w-0">
                  <p className="text-xs sm:text-sm text-gray-600">Monthly Rent</p>
                  <p className="text-lg sm:text-2xl font-bold truncate">
                    ${leases.length > 0 ? (leases[0]?.rent_amount || 0) : 'N/A'}
                  </p>
                </div>
                <DollarSign className="h-6 w-6 sm:h-8 sm:w-8 text-green-600 shrink-0" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-3 sm:pt-6 sm:p-6">
              <div className="flex items-center justify-between">
                <div className="min-w-0">
                  <p className="text-xs sm:text-sm text-gray-600">Next Payment</p>
                  <p className="text-sm sm:text-lg font-bold">
                    {(() => {
                      const pendingInvoice = invoices.find(inv => inv.status !== 'Paid');
                      if (pendingInvoice) {
                        return new Date(pendingInvoice.due_date).toLocaleDateString('en-US', { timeZone: 'America/New_York', month: 'short', day: 'numeric' });
                      }
                      return 'No dues';
                    })()}
                  </p>
                </div>
                <Calendar className="h-6 w-6 sm:h-8 sm:w-8 text-orange-600 shrink-0" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-3 sm:pt-6 sm:p-6">
              <div className="flex items-center justify-between">
                <div className="min-w-0">
                  <p className="text-xs sm:text-sm text-gray-600">Open Requests</p>
                  <p className="text-lg sm:text-2xl font-bold">{maintenanceRequests.filter(r => r.status !== 'Completed').length}</p>
                </div>
                <Wrench className="h-6 w-6 sm:h-8 sm:w-8 text-purple-600 shrink-0" />
              </div>
            </CardContent>
          </Card>
        </div>

        {loading && (
          <div className="text-center py-8">
            <p className="text-gray-600">Loading dashboard...</p>
          </div>
        )}

        {/* Main Content Tabs */}
        <Tabs defaultValue="payments" className="space-y-6">
          <div className="w-full">
            <TabsList className="inline-flex flex-wrap justify-center gap-1 h-auto p-1 w-full">
              <TabsTrigger value="payments" className="text-xs sm:text-sm py-2 px-3">Payments</TabsTrigger>
              <TabsTrigger value="maintenance" className="text-xs sm:text-sm py-2 px-3">Maintenance</TabsTrigger>
              <TabsTrigger value="lease" className="text-xs sm:text-sm py-2 px-3">Lease Docs</TabsTrigger>
              <TabsTrigger value="profile" className="text-xs sm:text-sm py-2 px-3">Profile</TabsTrigger>
            </TabsList>
          </div>

          {/* Payments Tab */}
          <TabsContent value="payments" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Make a Payment Card */}
              <Card className="lg:col-span-1" data-payments-section>
                <CardHeader>
                  <CardTitle>Make a Payment</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Autopay Status Card */}
                  {stripePromise && (
                    <div className={`p-4 rounded-lg border-2 ${autopayStatus.enabled ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'}`}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <RefreshCw className={`h-5 w-5 ${autopayStatus.enabled ? 'text-green-600' : 'text-gray-400'}`} />
                          <span className="font-semibold">Autopay</span>
                        </div>
                        {autopayStatus.enabled ? (
                          <Badge className="bg-green-500">Active</Badge>
                        ) : (
                          <Badge variant="outline" className="text-gray-500">Inactive</Badge>
                        )}
                      </div>
                      
                      {autopayStatus.enabled && autopayStatus.last_four ? (
                        <div className="space-y-2">
                          <p className="text-sm text-gray-600">
                            {autopayStatus.card_brand} •••• {autopayStatus.last_four}
                          </p>
                          <p className="text-xs text-green-700">
                            <CheckCircle className="inline h-3 w-3 mr-1" />
                            Your rent will be automatically charged each month
                          </p>
                          {showChangeCard ? (
                            <div className="mt-3 p-3 bg-white rounded border">
                              <p className="text-xs text-gray-600 mb-2">
                                Enter your new card below. It will replace {autopayStatus.card_brand} •••• {autopayStatus.last_four} for future autopay charges.
                              </p>
                              <Elements stripe={stripePromise}>
                                <AutopaySetupForm
                                  submitLabel="Save New Card"
                                  onSuccess={(data) => {
                                    setShowChangeCard(false);
                                    setAutopayStatus({
                                      enabled: true,
                                      last_four: data.last_four,
                                      card_brand: data.card_brand,
                                      has_payment_method: true,
                                    });
                                    toast({
                                      title: "Card Updated",
                                      description: `Autopay is now using ${data.card_brand} •••• ${data.last_four}.`,
                                    });
                                  }}
                                  onCancel={() => setShowChangeCard(false)}
                                />
                              </Elements>
                            </div>
                          ) : (
                            <div className="flex gap-2 mt-2">
                              <Button
                                variant="outline"
                                size="sm"
                                className="flex-1 text-blue-600 border-blue-200 hover:bg-blue-50"
                                onClick={() => setShowChangeCard(true)}
                                disabled={loadingAutopay}
                                data-testid="change-card-btn"
                              >
                                <RefreshCw className="h-4 w-4 mr-2" />
                                Change Card
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                className="flex-1 text-red-600 border-red-200 hover:bg-red-50"
                                onClick={handleDisableAutopay}
                                disabled={loadingAutopay}
                                data-testid="disable-autopay-btn"
                              >
                                {loadingAutopay ? (
                                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                ) : (
                                  <XCircle className="h-4 w-4 mr-2" />
                                )}
                                Disable Autopay
                              </Button>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="space-y-2">
                          <p className="text-sm text-gray-600">
                            Set up automatic payments for hassle-free rent collection
                          </p>
                          {!showAutopaySetup ? (
                            <Button
                              size="sm"
                              className="w-full bg-blue-600 hover:bg-blue-700"
                              onClick={() => {
                                const activeLease = leases.find(l => l.status === 'Active');
                                if (!activeLease) {
                                  toast({
                                    title: "No Active Lease",
                                    description: "You need an active lease to set up autopay",
                                    variant: "destructive"
                                  });
                                  return;
                                }
                                setShowAutopaySetup(true);
                              }}
                            >
                              <RefreshCw className="h-4 w-4 mr-2" />
                              Set Up Autopay
                            </Button>
                          ) : (
                            <Elements stripe={stripePromise}>
                              <AutopaySetupForm
                                onSuccess={(data) => {
                                  setShowAutopaySetup(false);
                                  setAutopayStatus({
                                    enabled: true,
                                    has_payment_method: true,
                                    last_four: data.last_four,
                                    card_brand: data.card_brand
                                  });
                                }}
                                onCancel={() => setShowAutopaySetup(false)}
                              />
                            </Elements>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Divider */}
                  {stripePromise && (
                    <div className="relative">
                      <div className="absolute inset-0 flex items-center">
                        <span className="w-full border-t" />
                      </div>
                      <div className="relative flex justify-center text-xs uppercase">
                        <span className="bg-white px-2 text-gray-500">Or pay now</span>
                      </div>
                    </div>
                  )}
                  
                  {/* Online Payment with Stripe */}
                  {!showStripePayment ? (
                    <>
                      {stripePromise && (
                        <div className="space-y-3">
                          {/* Invoice Selector - show if there are unpaid invoices */}
                          {invoices.filter(inv => inv.status !== 'Paid').length > 0 && (
                            <div>
                              <Label className="text-sm text-gray-600">Select invoice to pay (optional)</Label>
                              <Select 
                                value={stripeInvoice?.id || stripeInvoice?._id || 'general'}
                                onValueChange={(value) => {
                                  if (value === 'general') {
                                    setStripeInvoice(null);
                                  } else {
                                    const selectedInv = invoices.find(inv => (inv.id || inv._id) === value);
                                    setStripeInvoice(selectedInv);
                                  }
                                }}
                              >
                                <SelectTrigger className="w-full">
                                  <SelectValue placeholder="Select an invoice" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="general">General Payment (no invoice)</SelectItem>
                                  {invoices.filter(inv => inv.status !== 'Paid').map((inv) => (
                                    <SelectItem key={inv.id || inv._id} value={inv.id || inv._id}>
                                      {inv.description || inv.invoice_type} - ${inv.amount_due?.toFixed(2)} due
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          )}
                          <Button 
                            onClick={() => {
                              const activeLease = leases.find(l => l.status === 'Active');
                              if (!activeLease) {
                                toast({
                                  title: "No Active Lease",
                                  description: "You need an active lease to make a payment",
                                  variant: "destructive"
                                });
                                return;
                              }
                              setShowStripePayment(true);
                            }}
                            className="w-full bg-green-600 hover:bg-green-700"
                          >
                            <CreditCard className="mr-2 h-4 w-4" />
                            {stripeInvoice ? `Pay ${stripeInvoice.description || stripeInvoice.invoice_type} (up to $${stripeInvoice.amount_due?.toFixed(2)})` : 'Pay Online with Card'}
                          </Button>
                          <p className="text-xs text-gray-500 text-center">
                            {stripeInvoice ? 'Choose any amount — full or partial — on the next screen.' : 'One-time payment powered by Stripe'}
                          </p>
                        </div>
                      )}
                    </>
                  ) : stripePromise ? (
                    <Elements stripe={stripePromise}>
                      <StripePaymentForm
                        leaseId={leases.find(l => l.status === 'Active')?.id || leases.find(l => l.status === 'Active')?._id}
                        invoice={stripeInvoice}
                        autopayEnabledHint={!!autopayStatus?.enabled}
                        onSuccess={() => {
                          setShowStripePayment(false);
                          setStripeInvoice(null);
                          fetchData();
                          fetchAutopayStatus();
                        }}
                        onCancel={() => {
                          setShowStripePayment(false);
                          setStripeInvoice(null);
                        }}
                      />
                    </Elements>
                  ) : (
                    <div className="text-center p-4">
                      <p className="text-gray-500">Online payments not configured</p>
                      <Button
                        variant="outline"
                        onClick={() => {
                          setShowStripePayment(false);
                          setStripeInvoice(null);
                        }}
                        className="mt-2"
                      >
                        Back
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card className="lg:col-span-2">
                <CardHeader>
                  <CardTitle>Payment History</CardTitle>
                </CardHeader>
                <CardContent>
                  {payments.length === 0 ? (
                    <p className="text-center text-gray-600 py-8">No payments recorded yet</p>
                  ) : (
                    <div className="space-y-3">
                      {(showAllPayments ? payments : payments.slice(0, 3)).map((payment) => (
                        <div key={payment.id} className="flex items-center justify-between p-4 border rounded-lg">
                          <div>
                            <p className="font-semibold">{payment.description}</p>
                            <p className="text-sm text-gray-600">
                              {new Date(payment.payment_date || payment.created_at).toLocaleDateString('en-US', { timeZone: 'America/New_York' })}
                            </p>
                            <p className="text-xs text-gray-500">{payment.payment_method}</p>
                          </div>
                          <div className="text-right">
                            <p className="font-bold text-lg">${payment.amount}</p>
                            <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">{payment.status}</span>
                          </div>
                        </div>
                      ))}
                      {payments.length > 3 && (
                        <Button 
                          variant="ghost" 
                          className="w-full text-green-600 hover:text-green-700 hover:bg-green-50"
                          onClick={() => setShowAllPayments(!showAllPayments)}
                        >
                          {showAllPayments ? 'Show Less' : `View All (${payments.length} payments)`}
                        </Button>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Deposit Invoice Section */}
            {invoices.filter(inv => inv.invoice_type === 'deposit' && inv.status !== 'Paid').length > 0 && (
              <Card className="border-orange-200 bg-orange-50">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Receipt className="h-5 w-5 text-orange-600" />
                    Security Deposit Due
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {invoices.filter(inv => inv.invoice_type === 'deposit' && inv.status !== 'Paid').map((invoice) => (
                      <div key={invoice.id || invoice._id} className="p-4 bg-white rounded-lg border border-orange-200">
                        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                          <div>
                            <div className="flex items-center gap-2 mb-2">
                              <h4 className="font-semibold text-lg">Security Deposit</h4>
                              <Badge className={
                                invoice.status === 'Partial' ? 'bg-yellow-500' : 
                                invoice.status === 'Paid' ? 'bg-green-500' : 'bg-orange-500'
                              }>
                                {invoice.status}
                              </Badge>
                            </div>
                            <div className="text-sm text-gray-600 space-y-1">
                              <p>Total Amount: <span className="font-medium">${invoice.total_amount?.toFixed(2)}</span></p>
                              <p>Amount Paid: <span className="font-medium text-green-600">${invoice.amount_paid?.toFixed(2) || '0.00'}</span></p>
                              <p>Amount Due: <span className="font-bold text-orange-600">${invoice.amount_due?.toFixed(2)}</span></p>
                              <p>Due Date: {new Date(invoice.due_date).toLocaleDateString('en-US', { timeZone: 'America/New_York' })}</p>
                            </div>
                          </div>
                          <div className="flex flex-col gap-2">
                            {/* Pay Online with Card Button */}
                            {stripePromise && (
                              <Button 
                                onClick={() => {
                                  const activeLease = leases.find(l => l.status === 'Active');
                                  if (!activeLease) {
                                    toast({
                                      title: "No Active Lease",
                                      description: "You need an active lease to make a payment",
                                      variant: "destructive"
                                    });
                                    return;
                                  }
                                  setStripeInvoice(invoice);
                                  setShowStripePayment(true);
                                  // Scroll to payment section
                                  document.querySelector('[data-payments-section]')?.scrollIntoView({ behavior: 'smooth' });
                                }}
                                className="bg-green-600 hover:bg-green-700"
                              >
                                <CreditCard className="mr-2 h-4 w-4" />
                                Pay Online
                              </Button>
                            )}
                          </div>
                        </div>
                        
                        {/* Progress bar for deposit */}
                        <div className="mt-4">
                          <div className="flex justify-between text-xs text-gray-500 mb-1">
                            <span>Payment Progress</span>
                            <span>{((invoice.amount_paid || 0) / invoice.total_amount * 100).toFixed(0)}%</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div 
                              className="bg-green-500 h-2 rounded-full transition-all duration-300" 
                              style={{ width: `${((invoice.amount_paid || 0) / invoice.total_amount * 100)}%` }}
                            ></div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Rent Invoice Section */}
            {invoices.filter(inv => inv.invoice_type === 'rent' && inv.status !== 'Paid').length > 0 && (
              <Card className="border-blue-200 bg-blue-50">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Receipt className="h-5 w-5 text-blue-600" />
                    Rent Due
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {invoices.filter(inv => inv.invoice_type === 'rent' && inv.status !== 'Paid').map((invoice) => (
                      <div key={invoice.id || invoice._id} className="p-4 bg-white rounded-lg border border-blue-200">
                        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                          <div>
                            <div className="flex items-center gap-2 mb-2">
                              <h4 className="font-semibold text-lg">{invoice.description || 'Monthly Rent'}</h4>
                              <Badge className={
                                invoice.status === 'Partial' ? 'bg-yellow-500' : 
                                invoice.status === 'Paid' ? 'bg-green-500' : 'bg-blue-500'
                              }>
                                {invoice.status}
                              </Badge>
                            </div>
                            <div className="text-sm text-gray-600 space-y-1">
                              <p>Total Amount: <span className="font-medium">${invoice.total_amount?.toFixed(2)}</span></p>
                              <p>Amount Paid: <span className="font-medium text-green-600">${invoice.amount_paid?.toFixed(2) || '0.00'}</span></p>
                              <p>Amount Due: <span className="font-bold text-blue-600">${invoice.amount_due?.toFixed(2)}</span></p>
                              <p>Due Date: {new Date(invoice.due_date).toLocaleDateString('en-US', { timeZone: 'America/New_York' })}</p>
                            </div>
                          </div>
                          <div className="flex flex-col gap-2">
                            {/* Pay Online with Card Button */}
                            {stripePromise && (
                              <Button 
                                onClick={() => {
                                  const activeLease = leases.find(l => l.status === 'Active');
                                  if (!activeLease) {
                                    toast({
                                      title: "No Active Lease",
                                      description: "You need an active lease to make a payment",
                                      variant: "destructive"
                                    });
                                    return;
                                  }
                                  setStripeInvoice(invoice);
                                  setShowStripePayment(true);
                                  document.querySelector('[data-payments-section]')?.scrollIntoView({ behavior: 'smooth' });
                                }}
                                className="bg-green-600 hover:bg-green-700"
                              >
                                <CreditCard className="mr-2 h-4 w-4" />
                                Pay Online
                              </Button>
                            )}
                          </div>
                        </div>
                        
                        {/* Progress bar for rent */}
                        <div className="mt-4">
                          <div className="flex justify-between text-xs text-gray-500 mb-1">
                            <span>Payment Progress</span>
                            <span>{((invoice.amount_paid || 0) / invoice.total_amount * 100).toFixed(0)}%</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div 
                              className="bg-green-500 h-2 rounded-full transition-all duration-300" 
                              style={{ width: `${((invoice.amount_paid || 0) / invoice.total_amount * 100)}%` }}
                            ></div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Other Charges Section (late fees, utilities, etc.) */}
            {invoices.filter(inv => !['deposit', 'rent'].includes(inv.invoice_type) && inv.status !== 'Paid').length > 0 && (
              <Card className="border-purple-200">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Receipt className="h-5 w-5 text-purple-600" />
                    Other Charges
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {invoices.filter(inv => !['deposit', 'rent'].includes(inv.invoice_type) && inv.status !== 'Paid').map((invoice) => (
                      <div key={invoice.id || invoice._id} className="p-4 bg-white rounded-lg border border-purple-200">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <h4 className="font-semibold text-lg">{invoice.description || invoice.invoice_type?.replace('_', ' ').toUpperCase()}</h4>
                              <Badge className={
                                invoice.status === 'Partial' ? 'bg-yellow-500' : 
                                invoice.status === 'Paid' ? 'bg-green-500' : 'bg-purple-500'
                              }>
                                {invoice.status}
                              </Badge>
                            </div>
                            <div className="text-sm text-gray-600 space-y-1">
                              <p>Type: <span className="font-medium capitalize">{invoice.invoice_type?.replace('_', ' ')}</span></p>
                              <p>Total Amount: <span className="font-medium">${invoice.total_amount?.toFixed(2)}</span></p>
                              <p>Amount Paid: <span className="font-medium text-green-600">${invoice.amount_paid?.toFixed(2) || '0.00'}</span></p>
                              <p>Amount Due: <span className="font-bold text-purple-600">${invoice.amount_due?.toFixed(2)}</span></p>
                              <p>Due Date: {new Date(invoice.due_date).toLocaleDateString('en-US', { timeZone: 'America/New_York' })}</p>
                            </div>
                          </div>
                          <div className="ml-4 flex flex-col gap-2">
                            {/* Pay Online with Card Button */}
                            {stripePromise && (
                              <Button 
                                size="sm"
                                onClick={() => {
                                  const activeLease = leases.find(l => l.status === 'Active');
                                  if (!activeLease) {
                                    toast({
                                      title: "No Active Lease",
                                      description: "You need an active lease to make a payment",
                                      variant: "destructive"
                                    });
                                    return;
                                  }
                                  setStripeInvoice(invoice);
                                  setShowStripePayment(true);
                                  document.querySelector('[data-payments-section]')?.scrollIntoView({ behavior: 'smooth' });
                                }}
                                className="bg-green-600 hover:bg-green-700"
                              >
                                <CreditCard className="h-4 w-4 mr-1" />
                                Pay Online
                              </Button>
                            )}
                          </div>
                        </div>
                        
                        {/* Progress bar */}
                        <div className="mt-4">
                          <div className="flex justify-between text-xs text-gray-500 mb-1">
                            <span>Payment Progress</span>
                            <span>{((invoice.amount_paid || 0) / invoice.total_amount * 100).toFixed(0)}%</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div 
                              className="bg-green-500 h-2 rounded-full transition-all duration-300" 
                              style={{ width: `${((invoice.amount_paid || 0) / invoice.total_amount * 100)}%` }}
                            ></div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Maintenance Tab */}
          <TabsContent value="maintenance" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <Card className="lg:col-span-1">
                <CardHeader>
                  <CardTitle>Submit Request</CardTitle>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleMaintenanceSubmit} className="space-y-4">
                    <div>
                      <Label>Issue Title</Label>
                      <Input
                        required
                        value={maintenanceForm.title}
                        onChange={(e) => setMaintenanceForm({ ...maintenanceForm, title: e.target.value })}
                        placeholder="e.g., Leaking faucet"
                        disabled={uploadingMaintenance}
                      />
                    </div>
                    <div>
                      <Label>Priority</Label>
                      <Select 
                        value={maintenanceForm.priority} 
                        onValueChange={(val) => setMaintenanceForm({ ...maintenanceForm, priority: val })}
                        disabled={uploadingMaintenance}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Low">Low</SelectItem>
                          <SelectItem value="Medium">Medium</SelectItem>
                          <SelectItem value="High">High</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Description</Label>
                      <Textarea
                        required
                        value={maintenanceForm.description}
                        onChange={(e) => setMaintenanceForm({ ...maintenanceForm, description: e.target.value })}
                        placeholder="Describe the issue in detail..."
                        rows={4}
                        disabled={uploadingMaintenance}
                      />
                    </div>
                    
                    {/* Photo Upload Section */}
                    <div>
                      <Label className="flex items-center gap-2">
                        <Camera className="h-4 w-4" />
                        Photos (Optional)
                      </Label>
                      <p className="text-xs text-gray-500 mb-2">Add up to 5 photos to help explain the issue</p>
                      
                      {/* Photo Preview */}
                      {maintenancePhotos.length > 0 && (
                        <div className="flex flex-wrap gap-2 mb-3">
                          {maintenancePhotos.map((photo, index) => (
                            <div key={index} className="relative group">
                              <img
                                src={URL.createObjectURL(photo)}
                                alt={`Preview ${index + 1}`}
                                className="w-16 h-16 object-cover rounded border"
                              />
                              <button
                                type="button"
                                onClick={() => removeMaintenancePhoto(index)}
                                className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                                disabled={uploadingMaintenance}
                              >
                                <X className="h-3 w-3" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                      
                      {/* Add Photo Button */}
                      {maintenancePhotos.length < 5 && (
                        <label className={`flex items-center justify-center gap-2 p-3 border-2 border-dashed rounded-lg cursor-pointer hover:bg-gray-50 transition-colors ${uploadingMaintenance ? 'opacity-50 cursor-not-allowed' : ''}`}>
                          <input
                            type="file"
                            accept="image/*"
                            multiple
                            onChange={handleMaintenancePhotoChange}
                            className="hidden"
                            disabled={uploadingMaintenance}
                          />
                          <Image className="h-5 w-5 text-gray-400" />
                          <span className="text-sm text-gray-600">
                            {maintenancePhotos.length === 0 ? 'Add Photos' : `Add More (${5 - maintenancePhotos.length} remaining)`}
                          </span>
                        </label>
                      )}
                    </div>
                    
                    <Button 
                      type="submit" 
                      className="w-full bg-blue-600 hover:bg-blue-700"
                      disabled={uploadingMaintenance}
                    >
                      {uploadingMaintenance ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Submitting...
                        </>
                      ) : (
                        'Submit Request'
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>

              <Card className="lg:col-span-2">
                <CardHeader>
                  <CardTitle>Your Requests</CardTitle>
                </CardHeader>
                <CardContent>
                  {maintenanceRequests.length === 0 ? (
                    <div className="text-center py-8">
                      <Wrench className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                      <p className="text-gray-500">No maintenance requests yet</p>
                      <p className="text-sm text-gray-400">Submit a request using the form</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {maintenanceRequests.map((request) => (
                        <div key={request.id || request._id} className="p-4 border rounded-lg">
                          <div className="flex items-start justify-between mb-2">
                            <div>
                              <h4 className="font-semibold">{request.title || request.issue_type}</h4>
                              <p className="text-sm text-gray-600">{request.description}</p>
                            </div>
                            <span className={`text-xs px-2 py-1 rounded ${
                              request.status === 'Completed' ? 'bg-green-100 text-green-700' :
                              request.status === 'In Progress' ? 'bg-blue-100 text-blue-700' :
                              'bg-yellow-100 text-yellow-700'
                            }`}>
                              {request.status}
                            </span>
                          </div>
                          <div className="flex items-center justify-between text-sm text-gray-500">
                            <span>{new Date(request.submitted_at || request.created_at).toLocaleDateString('en-US', { timeZone: 'America/New_York' })}</span>
                            <span className={`font-medium ${
                              request.priority === 'High' ? 'text-red-600' :
                              request.priority === 'Medium' ? 'text-orange-600' :
                              'text-gray-600'
                            }`}>
                              {request.priority} Priority
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Lease Documents Tab */}
          <TabsContent value="lease">
            <div className="space-y-6">
              {/* Lease Signature Component - Only show if lease needs signing */}
              {leases.length > 0 && (
                <LeaseSignature 
                  lease={leases.find(l => l.status === 'Active') || leases[0]} 
                  onSignComplete={fetchData}
                />
              )}

              <Card>
                <CardHeader>
                  <CardTitle>Lease Documents</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {/* Signed Lease Agreement - with download */}
                    {(() => {
                      const activeLease = leases.find(l => l.status === 'Active') || leases[0];
                      return (
                        <div className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 transition-colors border-blue-200 bg-blue-50">
                          <div className="flex items-center space-x-3">
                            <FileText className="h-8 w-8 text-blue-600" />
                            <div>
                              <p className="font-semibold text-blue-800">Lease Agreement</p>
                              <p className="text-sm text-blue-600">
                                {activeLease?.lease_signed 
                                  ? `Signed on ${activeLease?.signed_at ? new Date(activeLease.signed_at).toLocaleDateString('en-US', { timeZone: 'America/New_York', month: 'long', day: 'numeric', year: 'numeric' }) : 'N/A'}`
                                  : 'Pending signature'}
                              </p>
                            </div>
                          </div>
                          <Button 
                            variant="outline"
                            className="border-blue-600 text-blue-600 hover:bg-blue-100"
                            onClick={() => {
                              const token = localStorage.getItem('access_token') || localStorage.getItem('session_token');
                              if (!token) {
                                toast({
                                  title: "Error",
                                  description: "Please log in to access the lease.",
                                  variant: "destructive"
                                });
                                return;
                              }
                              
                              const leaseId = activeLease?.id || activeLease?._id;
                              const baseUrl = process.env.REACT_APP_BACKEND_URL;
                              
                              if (activeLease?.lease_signed) {
                                // Download the signed lease
                                const downloadUrl = `${baseUrl}/api/leases/${leaseId}/signed-agreement?token=${encodeURIComponent(token)}`;
                                window.open(downloadUrl, '_blank');
                                toast({
                                  title: "Opening PDF... 📄",
                                  description: "The Lease Agreement will open in a new tab."
                                });
                              } else {
                                // Open the unsigned lease for preview/signing
                                const previewUrl = `${baseUrl}/api/leases/${leaseId}/unsigned-agreement?token=${encodeURIComponent(token)}`;
                                window.open(previewUrl, '_blank');
                                toast({
                                  title: "Opening Lease... 📄",
                                  description: "Review the lease, then use the signature box above to sign."
                                });
                              }
                            }}
                          >
                            <Download className="h-4 w-4 mr-2" />
                            {activeLease?.lease_signed ? 'Download PDF' : 'Sign First'}
                          </Button>
                        </div>
                      );
                    })()}
                  
                    {/* House Rules - Downloadable PDF */}
                    <div className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 transition-colors border-green-200 bg-green-50">
                      <div className="flex items-center space-x-3">
                        <BookOpen className="h-8 w-8 text-green-600" />
                        <div>
                          <p className="font-semibold text-green-800">House Rules and FAQs</p>
                          <p className="text-sm text-green-600">Important rules, Wi-Fi info, and contact details</p>
                        </div>
                      </div>
                      <Button 
                        variant="outline"
                        className="border-green-600 text-green-600 hover:bg-green-100"
                        onClick={() => {
                          // Get the token
                          const token = localStorage.getItem('access_token') || localStorage.getItem('session_token');
                          if (!token) {
                            toast({
                              title: "Error",
                              description: "Please log in to download house rules.",
                              variant: "destructive"
                            });
                            return;
                          }
                          
                          // Open PDF in new tab with token as query param
                          const baseUrl = process.env.REACT_APP_BACKEND_URL;
                          const downloadUrl = `${baseUrl}/api/tenant/house-rules-pdf/download?token=${encodeURIComponent(token)}`;
                          window.open(downloadUrl, '_blank');
                          
                          toast({
                            title: "Opening PDF... 📄",
                            description: "The House Rules PDF will open in a new tab.",
                            variant: "success"
                          });
                        }}
                      >
                        <Download className="h-4 w-4 mr-2" />
                        Download PDF
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Move-Out Checklist */}
              {leases.length > 0 && (
                <MoveOutChecklist lease={leases.find(l => l.status === 'Active') || leases[0]} />
              )}
            </div>
          </TabsContent>

          {/* Profile Tab */}
          <TabsContent value="profile">
            <div className="space-y-6">
              {/* Push Notifications disabled */}

              {/* Profile Information */}
              <Card>
                <CardHeader>
                  <CardTitle>Profile Information</CardTitle>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleUpdateProfile} className="space-y-4 max-w-2xl">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <Label>Full Name</Label>
                        <Input value={user?.name || ''} disabled className="bg-gray-100" />
                      </div>
                      <div>
                        <Label>Email {isEditingProfile && <span className="text-red-500">*</span>}</Label>
                        <Input 
                          type="email"
                          value={isEditingProfile ? profileForm.email : (user?.email || '')} 
                          onChange={(e) => setProfileForm({...profileForm, email: e.target.value})}
                          disabled={!isEditingProfile}
                          className={!isEditingProfile ? 'bg-gray-100' : ''}
                          required
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <Label>Phone Number {isEditingProfile && <span className="text-red-500">*</span>}</Label>
                        <Input 
                          type="tel"
                          value={isEditingProfile ? profileForm.phone : (user?.phone || 'Not set')} 
                          onChange={(e) => setProfileForm({...profileForm, phone: e.target.value})}
                          disabled={!isEditingProfile}
                          className={!isEditingProfile ? 'bg-gray-100' : ''}
                          placeholder="Enter phone number"
                        />
                      </div>
                      <div>
                        <Label>Current Room</Label>
                        <Input value={leases.length > 0 ? (leases[0]?.room_title || leases[0]?.room_number || 'Room') : 'No Lease'} disabled className="bg-gray-100" />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <Label>Monthly Rent</Label>
                        <Input value={leases.length > 0 ? `$${leases[0]?.rent_amount || 0}` : 'N/A'} disabled className="bg-gray-100" />
                      </div>
                      <div>
                        <Label>Lease Status</Label>
                        <Input value={leases.length > 0 ? leases[0]?.status || 'Unknown' : 'No Lease'} disabled className="bg-gray-100" />
                      </div>
                    </div>
                    
                    {isEditingProfile ? (
                      <div className="flex gap-2">
                        <Button 
                          type="submit" 
                          className="bg-green-600 hover:bg-green-700"
                          disabled={updatingProfile}
                        >
                          {updatingProfile ? (
                            <>
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              Saving...
                            </>
                          ) : (
                            'Save Changes'
                          )}
                        </Button>
                        <Button 
                          type="button"
                          variant="outline"
                          onClick={() => {
                            setIsEditingProfile(false);
                            setProfileForm({
                              email: user?.email || '',
                              phone: user?.phone || ''
                            });
                          }}
                        >
                          Cancel
                        </Button>
                      </div>
                    ) : (
                      <Button 
                        type="button"
                        className="bg-green-600 hover:bg-green-700"
                        onClick={() => setIsEditingProfile(true)}
                      >
                        Update Profile
                      </Button>
                    )}
                  </form>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default TenantDashboard;